/**
 * The phone / email row shared by the handoff cards.
 *
 * The bell dropdown and the dashboard card render the same information and
 * should look identical doing it. They had drifted: the dashboard made the phone
 * a `tel:` link at `text-foreground/80`, while the bell rendered it as plain
 * `text-muted-foreground` that could not be tapped — on a phone, the one surface
 * where tapping the number matters most.
 *
 * Both are links here, underlined so they read as tappable next to the
 * underlined contact name above them.
 */

import React from 'react';
import { Mail, Phone } from 'lucide-react';
import { HandoffLocationLabel } from './HandoffMetaLine';
import { HandoffLocation } from '../../../types/handoff.types';
import { formatPhoneNumber } from '../../../utils/formatters';
import { cn } from '../../../utils/cn';

interface HandoffContactLineProps {
  phone?: string | null;
  email?: string | null;
  /** Where the lead is. Sits with the phone number rather than the name. */
  location?: HandoffLocation | null;
  className?: string;
}

const link =
  'inline-flex items-center gap-1.5 underline decoration-foreground/25 underline-offset-2 hover:text-primary hover:decoration-primary';

export const HandoffContactLine: React.FC<HandoffContactLineProps> = ({
  phone,
  email,
  location,
  className,
}) => {
  if (!phone && !email && !location) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-foreground/80',
        className
      )}
    >
      {phone && (
        <a href={`tel:${phone}`} className={link}>
          <Phone className="h-3.5 w-3.5 shrink-0" />
          {formatPhoneNumber(phone)}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className={cn(link, 'min-w-0')}>
          <Mail className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate max-w-[160px] sm:max-w-[220px]">{email}</span>
        </a>
      )}
      <HandoffLocationLabel location={location} />
    </div>
  );
};

export default HandoffContactLine;
